var __makeTemplateObject = this && this.__makeTemplateObject || function (cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }
  return cooked;
};
import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { maxBy } from 'lodash';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, clockAngle, cPointAtOrigin } from '@shapeable/utils';
import { useComponentProps } from '../context/component-props-context';
import { useStaticColors } from '../hooks/use-theme-colors';
import { SvgCircle } from './svg-circle';
import { SvgTextArc } from './svg-text-arc';
import { SvgLine } from './svg-line';
import { SvgSegment } from './svg-segment';
import { useTooltipEffect } from '../hooks/use-tooltip-effect';
import ReactTooltip from 'react-tooltip';
import { MarkdownContent } from './markdown-content';
var cls = classNames('svg-segment-chart');
export var SvgSegmentChartDefaultProps = {
  padding: 20,
  // padding between the labels and the boundary of the chart
  outerRadius: 225,
  animate: true,
  results: [],
  segmentMode: 'multiple',
  maxValue: 9,
  label: {
    fontSize: 18,
    fontFamily: 'sans',
    colors: {
      fill: 'subtle'
    },
    gap: 20
  },
  divider: {
    colors: {
      stroke: 'light'
    },
    strokeWidth: 1
  },
  ring: {
    count: 9,
    colors: {
      stroke: 'light'
    },
    strokeWidth: 1
  },
  majorRing: {
    interval: 3,
    colors: {
      stroke: 'light'
    },
    strokeWidth: 3
  }
};
// -------- Styles -------->
var ContainerStyles = breakpoints({
  base: css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    width: 100%;\n  "], ["\n    width: 100%;\n  "])))
});
var RingStyles = breakpoints({
  base: css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    \n  "], ["\n    \n  "])))
});
var LabelStyles = breakpoints({
  base: css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    ", "\n  "], ["\n    ", "\n  "])), function (_a) {
    var fontFamily = _a.fontFamily,
      fontSize = _a.fontSize;
    return css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n      font-family: ", ";\n      font-size: ", ";\n    "], ["\n      font-family: ", ";\n      font-size: ", ";\n    "])), theme.FONT(fontFamily), theme.FONT_SIZE(fontSize));
  })
});
var SegmentStyles = breakpoints({
  base: css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n    \n  "], ["\n    \n  "])))
});
var TooltipStyles = breakpoints({
  base: css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n    width: 160px;\n  "], ["\n    width: 160px;\n  "]))),
  tablet: css(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n    width: 220px;\n  "], ["\n    width: 220px;\n  "]))),
  desktop: css(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n    width: 280px;\n  "], ["\n    width: 280px;\n  "])))
});
var ContentStyles = breakpoints({
  base: css(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n    padding: ", " 0;\n  "], ["\n    padding: ", " 0;\n  "])), theme.UNIT(2))
});
var TooltipsStyles = breakpoints({
  base: css(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n    .__react_component_tooltip {\n      border-radius: ", " !important;\n    }\n  "], ["\n    .__react_component_tooltip {\n      border-radius: ", " !important;\n    }\n  "])), theme.UNIT(4))
});
var SegmentGroupStyles = breakpoints({
  base: css(templateObject_11 || (templateObject_11 = __makeTemplateObject(["\n    outline: none;\n    transition: filter 0.3s;\n    &:hover {\n      filter: brightness(1.1);\n    }\n  "], ["\n    outline: none;\n    transition: filter 0.3s;\n    &:hover {\n      filter: brightness(1.1);\n    }\n  "])))
});
// -------- Components -------->
var My = {
  NAME: 'SvgSegmentChart',
  Container: styled.svg(templateObject_12 || (templateObject_12 = __makeTemplateObject(["", ""], ["", ""])), ContainerStyles),
  Ring: styled(SvgCircle)(templateObject_13 || (templateObject_13 = __makeTemplateObject(["", ""], ["", ""])), RingStyles),
  Divider: styled(SvgLine)(templateObject_14 || (templateObject_14 = __makeTemplateObject(["", ""], ["", ""])), RingStyles),
  Label: styled(SvgTextArc)(templateObject_15 || (templateObject_15 = __makeTemplateObject(["", ""], ["", ""])), LabelStyles),
  Tooltips: styled.div(templateObject_16 || (templateObject_16 = __makeTemplateObject(["", ""], ["", ""])), TooltipsStyles),
  Tooltip: styled(ReactTooltip)(templateObject_17 || (templateObject_17 = __makeTemplateObject(["", ""], ["", ""])), TooltipStyles),
  TooltipContent: styled(MarkdownContent)(templateObject_18 || (templateObject_18 = __makeTemplateObject(["", ""], ["", ""])), ContentStyles),
  SegmentGroup: styled.g(templateObject_19 || (templateObject_19 = __makeTemplateObject(["", ""], ["", ""])), SegmentGroupStyles),
  Segment: styled(SvgSegment)(templateObject_20 || (templateObject_20 = __makeTemplateObject(["", ""], ["", ""])), SegmentStyles)
};
export var SvgSegmentChart = function (props) {
  var componentProps = useComponentProps(My.NAME, SvgSegmentChartDefaultProps, props);
  var className = componentProps.className,
    ring = componentProps.ring,
    majorRing = componentProps.majorRing,
    children = componentProps.children,
    label = componentProps.label,
    divider = componentProps.divider,
    outerRadius = componentProps.outerRadius,
    animate = componentProps.animate,
    padding = componentProps.padding,
    results = componentProps.results,
    segmentMode = componentProps.segmentMode,
    springId = componentProps.springId;
  var labelColors = useStaticColors(componentProps.label.colors);
  useTooltipEffect();
  // dictionary to record all spring states. we need to use a ref here, because regular state forces a re-render
  var animatedSpringValues = useRef({});
  var radiusStep = outerRadius / (ring.count || 1); // fallback to 1 if someone tries to pass 0
  var ringItems = Array.from({
    length: ring.count
  }, function (_, i) {
    return i;
  });
  var angleStep = 360 / results.length;
  var totalRadius = outerRadius + label.gap + label.fontSize + padding;
  var origin = {
    x: totalRadius,
    y: totalRadius
  };
  var cPoint = cPointAtOrigin(origin.x, origin.y);
  var viewBox = "0 0 ".concat(totalRadius * 2, " ").concat(totalRadius * 2);
  var maxValueItem = maxBy(results, function (v) {
    return v.value;
  });
  var maxValue = componentProps.maxValue || (maxValueItem === null || maxValueItem === void 0 ? void 0 : maxValueItem.value);
  // const { triggerRef, visible, position, content } = useTooltip({ delay: 500 });
  var lineSpringConfig = {
    duration: 400
  };
  return React.createElement(React.Fragment, null, React.createElement(My.Container, {
    viewBox: viewBox,
    className: cls.name(className)
  }, /* ------ Render the Labels and Dividers ------ */
  results.map(function (result, i) {
    var labelRadius = outerRadius + label.gap;
    var dividerRadius = outerRadius;
    var dividerAngle = clockAngle(angleStep * i);
    var labelPoint1 = cPoint(labelRadius, dividerAngle);
    var labelPoint2 = cPoint(labelRadius, dividerAngle + angleStep);
    var linePoint = cPoint(dividerRadius, dividerAngle);
    var valueInnerRadius = 0;
    var valueOuterRadius = valueInnerRadius + result.value / maxValue * outerRadius;
    var valueStartAngle = clockAngle(angleStep * i);
    var valueEndAngle = clockAngle(angleStep * (i + 1));
    var segments = Math.floor(result.value);
    var segmentItems = Array.from({
      length: segments
    }, function (_, i) {
      return i;
    });
    var dividerSpringId = "".concat(springId, "-divider-").concat(i);
    var singleSegmentSpringId = "".concat(springId, "-segment-").concat(i);
    return React.createElement(React.Fragment, null, React.createElement(My.Divider, {
      springId: dividerSpringId,
      key: dividerSpringId,
      springConfig: lineSpringConfig,
      x1: origin.x,
      y1: origin.y,
      x2: linePoint.cx,
      y2: linePoint.cy,
      opacity: 1,
      animate: animate,
      from: {
        x1: origin.x,
        y1: origin.y,
        x2: origin.x,
        y2: origin.y,
        opacity: 0
      },
      colors: divider.colors,
      strokeWidth: divider.strokeWidth
    }), React.createElement(My.SegmentGroup, {
      "data-for": result.id,
      "data-tip": ""
    }, segmentMode === 'multiple' ? segmentItems.map(function (j) {
      var valueInnerRadius = j * radiusStep;
      var valueOuterRadius = valueInnerRadius + radiusStep;
      var valueStartAngle = clockAngle(angleStep * i);
      var valueEndAngle = clockAngle(angleStep * (i + 1));
      var segmentSpringId = "".concat(springId, "-segment-").concat(i, "-").concat(j);
      return React.createElement(My.Segment, {
        delay: 800 + 105 * j,
        springId: segmentSpringId,
        key: segmentSpringId,
        springConfig: {
          duration: 200
        },
        animate: animate,
        from: {
          innerRadius: valueInnerRadius,
          outerRadius: valueInnerRadius,
          startAngle: valueStartAngle,
          endAngle: valueEndAngle,
          opacity: 0
        },
        colors: result.colors,
        origin: origin,
        opacity: 1,
        innerRadius: valueInnerRadius,
        outerRadius: valueOuterRadius,
        startAngle: valueStartAngle,
        endAngle: valueEndAngle
      });
    }) : React.createElement(My.Segment, {
      springId: singleSegmentSpringId,
      delay: 600,
      animate: animate,
      key: singleSegmentSpringId,
      springConfig: {
        duration: 600
      },
      from: {
        innerRadius: 0,
        outerRadius: 0,
        startAngle: valueStartAngle,
        endAngle: valueEndAngle,
        opacity: 0
      },
      colors: result.colors,
      origin: origin,
      opacity: 1,
      innerRadius: valueInnerRadius,
      outerRadius: valueOuterRadius,
      startAngle: clockAngle(angleStep * i),
      endAngle: clockAngle(angleStep * (i + 1))
    })), React.createElement(My.Label, {
      springId: "".concat(springId, "-label-").concat(i),
      animate: animate,
      x1: labelPoint1.cx,
      y1: labelPoint1.cy,
      x2: labelPoint2.cx,
      y2: labelPoint2.cy,
      r: labelRadius,
      from: {
        x1: origin.x,
        y1: origin.y,
        x2: origin.x,
        y2: origin.y,
        opacity: 0
      },
      color: labelColors.fill,
      fontFamily: label.fontFamily,
      fontSize: label.fontSize,
      springConfig: lineSpringConfig,
      id: i.toString(),
      key: "label-".concat(i)
    }, result.label.toUpperCase()));
  }), /* ------ Render the Rings ------ */
  ringItems.map(function (i) {
    var isMajorRing = (i + 1) % majorRing.interval === 0;
    var radius = (i + 1) * radiusStep;
    var colors = isMajorRing ? majorRing.colors : ring.colors;
    var strokeWidth = isMajorRing ? majorRing.strokeWidth : ring.strokeWidth;
    var ringSpringId = "".concat(springId, "-ring-").concat(i);
    return React.createElement(My.Ring, {
      springId: ringSpringId,
      animate: animate,
      key: ringSpringId,
      springConfig: lineSpringConfig,
      from: {
        r: 0,
        opacity: 0
      },
      colors: colors,
      opacity: 1,
      cx: origin.x,
      cy: origin.y,
      r: radius,
      strokeWidth: strokeWidth
    });
  })), React.createElement(My.Tooltips, null, results.map(function (result, i) {
    var _a;
    return React.createElement(My.Tooltip, {
      delayHide: 300,
      delayShow: 100,
      effect: "solid",
      offset: {
        top: -20,
        left: 0
      },
      backgroundColor: "#FFFFFFF9",
      textColor: '#333333',
      clickable: true,
      wrapper: "span",
      id: result.id
    }, React.createElement(My.TooltipContent, {
      text: (_a = result.description) === null || _a === void 0 ? void 0 : _a.text
    }));
  })));
};
SvgSegmentChart.cls = cls;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10, templateObject_11, templateObject_12, templateObject_13, templateObject_14, templateObject_15, templateObject_16, templateObject_17, templateObject_18, templateObject_19, templateObject_20;